import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { 
  LightBulbIcon, 
  CheckIcon, 
  FlagIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { goalsService } from '../../services/goals.service';

export interface GoalSuggestion {
  title: string;
  description: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  difficulty: 'easy' | 'medium' | 'hard';
  impact: 'low' | 'medium' | 'high';
  skillsToImprove: string[];
  targetSessions: number;
  targetScore?: number;
  reasoning: string;
  confidence: number;
  estimatedHours: number;
  dueDate: string;
  tags: string[];
}

interface GoalSuggestionModalProps {
  visible: boolean;
  onHide: () => void;
  suggestions: GoalSuggestion[];
  sessionTitle: string;
  onGoalCreated?: () => void;
}

const GoalSuggestionModal: React.FC<GoalSuggestionModalProps> = ({
  visible,
  onHide,
  suggestions,
  sessionTitle,
  onGoalCreated
}) => {
  const [creatingGoals, setCreatingGoals] = useState<Set<string>>(new Set());
  const [createdGoals, setCreatedGoals] = useState<Set<string>>(new Set());

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'hard': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'easy': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'bg-blue-100 text-blue-800';
      case 'medium': return 'bg-purple-100 text-purple-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleCreateGoal = async (suggestion: GoalSuggestion) => {
    try {
      setCreatingGoals(prev => new Set(prev).add(suggestion.title));

      const goalData = {
        title: suggestion.title,
        description: suggestion.description,
        category: suggestion.category,
        priority: suggestion.priority,
        difficulty: suggestion.difficulty,
        impact: suggestion.impact,
        dueDate: suggestion.dueDate,
        estimatedHours: suggestion.estimatedHours,
        tags: suggestion.tags,
        skillsToImprove: suggestion.skillsToImprove,
        targetSessions: suggestion.targetSessions,
        targetScore: suggestion.targetScore
      };

      await goalsService.createGoal(goalData);
      setCreatedGoals(prev => new Set(prev).add(suggestion.title));
      
      if (onGoalCreated) {
        onGoalCreated();
      }
    } catch (error) {
      console.error('Error creating goal:', error);
    } finally {
      setCreatingGoals(prev => {
        const newSet = new Set(prev);
        newSet.delete(suggestion.title);
        return newSet;
      });
    }
  };

  const handleCreateAllGoals = async () => {
    const uncreatedSuggestions = suggestions.filter(s => !createdGoals.has(s.title));
    
    for (const suggestion of uncreatedSuggestions) {
      await handleCreateGoal(suggestion);
    }
  };

  const handleDismissAll = () => {
    onHide();
  };

  return (
    <Dialog
      header={
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <LightBulbIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Smart Goal Suggestions</h2>
            <p className="text-sm text-gray-600">Based on your "{sessionTitle}" session</p>
          </div>
        </div>
      }
      visible={visible}
      onHide={onHide}
      style={{ width: '90vw', maxWidth: '800px' }}
      className="goal-suggestion-modal"
      draggable={false}
      resizable={false}
    >
      <div className="space-y-6">
        {/* Introduction */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <LightBulbIcon className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">AI-Powered Recommendations</h3>
              <p className="text-sm text-blue-700 mt-1">
                We've analyzed your session performance and identified {suggestions.length} goal{suggestions.length > 1 ? 's' : ''} that could help you advance your skills. 
                Review the suggestions below and create the ones that align with your learning objectives.
              </p>
            </div>
          </div>
        </div>

        {/* Suggestions List */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <Card key={index} className="relative">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{suggestion.title}</h3>
                      {createdGoals.has(suggestion.title) && (
                        <Badge value="Created" severity="success" />
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{suggestion.description}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {Math.round(suggestion.confidence * 100)}% confidence
                      </div>
                      <ProgressBar 
                        value={suggestion.confidence * 100} 
                        style={{ height: '4px', width: '60px' }}
                        showValue={false}
                      />
                    </div>
                  </div>
                </div>

                {/* Tags and Metadata */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(suggestion.priority)}`}>
                    {suggestion.priority} priority
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(suggestion.difficulty)}`}>
                    {suggestion.difficulty} difficulty
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(suggestion.impact)}`}>
                    {suggestion.impact} impact
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {suggestion.category}
                  </span>
                </div>

                {/* Skills and Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Skills to Improve</h4>
                    <div className="flex flex-wrap gap-1">
                      {suggestion.skillsToImprove.map((skill, skillIndex) => (
                        <span key={skillIndex} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <FlagIcon className="h-4 w-4" />
                      <span>{suggestion.targetSessions} target sessions</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <ClockIcon className="h-4 w-4" />
                      <span>{suggestion.estimatedHours}h estimated</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <StarIcon className="h-4 w-4" />
                      <span>Due {formatDate(suggestion.dueDate)}</span>
                    </div>
                  </div>
                </div>

                {/* Reasoning */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-gray-900 mb-1">Why this goal?</h4>
                  <p className="text-sm text-gray-600">{suggestion.reasoning}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex space-x-2">
                    {!createdGoals.has(suggestion.title) ? (
                      <Button
                        label="Create Goal"
                        icon="pi pi-plus"
                        size="small"
                        loading={creatingGoals.has(suggestion.title)}
                        onClick={() => handleCreateGoal(suggestion)}
                        className="p-button-sm"
                      />
                    ) : (
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">Goal Created</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-600">
            {createdGoals.size} of {suggestions.length} goals created
          </div>
          <div className="flex space-x-3">
            <Button
              label="Dismiss All"
              icon="pi pi-times"
              severity="secondary"
              size="small"
              onClick={handleDismissAll}
            />
            {createdGoals.size < suggestions.length && (
              <Button
                label="Create All Goals"
                icon="pi pi-check"
                size="small"
                onClick={handleCreateAllGoals}
                loading={creatingGoals.size > 0}
              />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default GoalSuggestionModal;
